'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useSessionStore } from '@stores/session/session.store'

import { useInitializationContext } from '../contexts/InitializationContext'
import { EMAIL_VERIFICATION_POLL_INTERVAL_MS } from '../core.const'

import { useAuthUser } from './useAuthUser'

type UsePollEmailVerificationOptions = {
  onVerified?: () => void | Promise<void>
}

export function usePollEmailVerification(options: UsePollEmailVerificationOptions = {}) {
  const { invalidate, user: auth0User } = useAuthUser()
  const { isEmailVerified, setIsEmailVerified } = useInitializationContext()
  const [isChecking, setIsChecking] = useState(true)
  const isCheckInFlightRef = useRef(false)
  const lastCheckAtRef = useRef(0)
  const invalidateRef = useRef(invalidate)
  const onVerifiedRef = useRef(options.onVerified)

  useEffect(() => {
    invalidateRef.current = invalidate
  }, [invalidate])

  useEffect(() => {
    onVerifiedRef.current = options.onVerified
  }, [options.onVerified])

  const isAlreadyVerified = isEmailVerified || auth0User?.email_verified === true

  const checkEmailVerification = useCallback(
    async (pollOptions?: { force?: boolean }) => {
      if (isAlreadyVerified || isCheckInFlightRef.current) return

      const now = Date.now()
      if (!pollOptions?.force && now - lastCheckAtRef.current < EMAIL_VERIFICATION_POLL_INTERVAL_MS) return

      isCheckInFlightRef.current = true
      lastCheckAtRef.current = now
      setIsChecking(true)

      try {
        const response = await fetch('/api/auth/refresh-session', {
          method: 'POST',
          credentials: 'same-origin',
        })
        const data = (await response.json()) as Record<string, unknown>

        if (data?.email_verified !== true) {
          return
        }

        useSessionStore.getState().clearToken()
        await invalidateRef.current()
        setIsEmailVerified(true)
        await onVerifiedRef.current?.()
      } catch {
        // keep polling on the next interval
      } finally {
        isCheckInFlightRef.current = false
        setIsChecking(false)
      }
    },
    [isAlreadyVerified, setIsEmailVerified]
  )

  useEffect(() => {
    if (isAlreadyVerified) {
      setIsChecking(false)
      return
    }

    const handleReturnToTab = () => {
      if (document.visibilityState === 'visible') {
        void checkEmailVerification({ force: true })
      }
    }

    void checkEmailVerification({ force: true })

    const intervalId = window.setInterval(() => {
      void checkEmailVerification()
    }, EMAIL_VERIFICATION_POLL_INTERVAL_MS)

    document.addEventListener('visibilitychange', handleReturnToTab)
    window.addEventListener('focus', handleReturnToTab)

    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleReturnToTab)
      window.removeEventListener('focus', handleReturnToTab)
    }
  }, [checkEmailVerification, isAlreadyVerified])

  return { isChecking }
}
